import { useContext } from 'react';
import clsx from 'clsx';

import FavoritesIconButton from '@atom/components/common/favorites/FavoritesIconButton';
import UserThumbnail from '@atom/components/common/UserThumbnail';
import AddTeamModalContext from '@atom/components/common/workOrderDetail/addTeamModal/AddTeamModalContext';
import { Icon } from '@atom/mui';
import { CheckBoxIcon, CheckBoxOutlineBlankIcon } from '@atom/mui/icons';
import colors from '@atom/styles/colors';
import { UserDetail } from '@atom/types/user';

import classes from './usersTab.module.css';

export interface Props {
  user: UserDetail;
}

const UserRow = ({ user }: Props) => {
  const {
    usersOnTask,
    favoriteUsers,
    selectedUsers,
    removeUserFromCart,
    addUserToCart,
    createFavorite,
    favoritesEnabled,
    favoritesLists,
    handleAddList,
    updateFavorite,
  } = useContext(AddTeamModalContext);

  const isOnTask = usersOnTask.includes(user.id);
  const isSelected = selectedUsers
    .map(selectedUser => selectedUser.id)
    .includes(user.id);
  const subtextStyle = clsx(classes.subtext, isOnTask && classes.disabled);

  const onClick = () => {
    if (isSelected) {
      return removeUserFromCart(user.id);
    }

    if (isOnTask) {
      return () => {};
    }

    return addUserToCart(user);
  };

  const checkbox = isOnTask ? (
    <Icon color={colors.neutral.ash} icon={CheckBoxIcon} />
  ) : isSelected ? (
    <Icon color={colors.brand.blue} icon={CheckBoxIcon} />
  ) : (
    <Icon color={colors.neutral.gray} icon={CheckBoxOutlineBlankIcon} />
  );

  return (
    <div
      className={classes.userRow}
      onClick={onClick}
      data-cy={`addTeamUserRow-${user.id}`}
    >
      {checkbox}
      <div className={classes.leftContainer}>
        <UserThumbnail
          image={user.photoUrl}
          alt={user.firstName}
          disabled={isOnTask}
        />
        <div>
          <div
            className={clsx(
              classes.title,
              isSelected && classes.selected,
              isOnTask && classes.disabled,
            )}
          >{`${user.firstName} ${user.lastName}`}</div>
          <div className={subtextStyle}>{user.email}</div>
        </div>
      </div>
      {favoritesEnabled && (
        <FavoritesIconButton
          item={user}
          createFavorite={createFavorite}
          updateFavorite={updateFavorite}
          favorites={favoriteUsers}
          favoritesLists={favoritesLists}
          typeName="users"
          handleAddList={handleAddList}
          canAddFavorite
          canAddList
        />
      )}
    </div>
  );
};

export default UserRow;
